import React from 'react'
import Button from '@atlaskit/button'
import Lozenge from '@atlaskit/lozenge'
import DropdownMenu, { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu'
import Tooltip from '@atlaskit/tooltip'
import Spinner from '@atlaskit/spinner'
import MoreIcon from '@atlaskit/icon/glyph/more'
import FolderIcon from '@atlaskit/icon/glyph/folder'
import DocumentsIcon from '@atlaskit/icon/glyph/documents'
import EditorPanelIcon from '@atlaskit/icon/glyph/editor/panel'
import CalendarIcon from '@atlaskit/icon/glyph/calendar'
import ShipIcon from '@atlaskit/icon/glyph/ship'
import { formatDate } from '../../utils/dataTransformer'

/**
 * RELEASE CARD COMPONENT
 *
 * Displays a single release with its metadata and progress.
 * Features:
 * - Release name, description, status
 * - Stats: folder count, scenario count, run count
 * - Progress bar showing all 5 statuses (Pass, Fail, Blocked, Query, Exclude)
 * - Context menu: Edit, Clone, Mark as Released, Archive/Unarchive, Delete
 */
function ReleaseCard({
  release,
  onOpen,
  onEdit,
  onArchive,
  onUnarchive,
  onMarkReleased,
  onMarkActive,
  onClone,
  onDelete,
  onViewReport,
  isArchived = false,
  isCloning = false
}) {
  const {
    id,
    name,
    description,
    status,
    folderCount = 0,
    scenarioCount = 0,
    runCount = 0,
    statusCounts = {},
    createdOn
  } = release

  // Status color mapping for progress bar
  const statusColors = {
    Pass: '#36B37E',      // Green
    Fail: '#DE350B',      // Red
    Blocked: '#E06C00',   // Orange
    Query: '#0065FF',     // Blue
    Exclude: '#6B778C'    // Gray
  }

  // Calculate progress percentages for all 5 statuses
  const totalExecutions = Object.values(statusCounts).reduce((sum, count) => sum + count, 0)
  const passCount = statusCounts.Pass || 0
  const failCount = statusCounts.Fail || 0
  const blockedCount = statusCounts.Blocked || 0
  const queryCount = statusCounts.Query || 0
  const excludeCount = statusCounts.Exclude || 0

  const passPercent = totalExecutions > 0 ? (passCount / totalExecutions) * 100 : 0
  const failPercent = totalExecutions > 0 ? (failCount / totalExecutions) * 100 : 0
  const blockedPercent = totalExecutions > 0 ? (blockedCount / totalExecutions) * 100 : 0
  const queryPercent = totalExecutions > 0 ? (queryCount / totalExecutions) * 100 : 0
  const excludePercent = totalExecutions > 0 ? (excludeCount / totalExecutions) * 100 : 0

  // Build tooltip text showing non-zero status counts separated by dots
  const progressTooltip = [
    passCount > 0 && `Pass: ${passCount}`,
    failCount > 0 && `Fail: ${failCount}`,
    blockedCount > 0 && `Blocked: ${blockedCount}`,
    queryCount > 0 && `Query: ${queryCount}`,
    excludeCount > 0 && `Exclude: ${excludeCount}`
  ].filter(Boolean).join(' · ')

  // Get status lozenge appearance
  const getStatusAppearance = (status) => {
    switch (status) {
      case 'Active':
        return 'success'
      case 'Draft':
        return 'default'
      case 'Released':
        return 'inprogress'
      case 'Archived':
        return 'moved'
      default:
        return 'default'
    }
  }

  return (
    <div
      className={`release-card${isArchived ? ' release-card-archived' : ''}`}
      onClick={onOpen}
    >
      {/* Header Row: Name + Status + Actions */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        marginBottom: '4px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ShipIcon label="Release" size="medium" primaryColor="var(--ds-icon-brand, #0052CC)" />
          <h3 style={{
            fontSize: '16px',
            fontWeight: 600,
            color: 'var(--ds-text, #172B4D)',
            margin: 0
          }}>
            {name}
          </h3>
          <Lozenge appearance={getStatusAppearance(isArchived ? 'Archived' : status)}>
            {isArchived ? 'ARCHIVED' : status?.toUpperCase() || 'DRAFT'}
          </Lozenge>
          {isCloning && (
            <Tooltip content="Cloning this release...">
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <Spinner size="small" />
                <span style={{
                  fontSize: '12px',
                  color: 'var(--ds-text-subtle, #5E6C84)',
                  fontWeight: 500
                }}>
                  Cloning...
                </span>
              </span>
            </Tooltip>
          )}
        </div>

        {/* Actions Menu */}
        <div onClick={(e) => e.stopPropagation()}>
          <DropdownMenu
            trigger={({ triggerRef, ...props }) => (
              <Button
                {...props}
                ref={triggerRef}
                appearance="subtle"
                iconBefore={<MoreIcon label="More actions" size="small" />}
              />
            )}
            shouldRenderToParent
          >
            <DropdownItemGroup>
              {/* View Report - opens execution summary report */}
              {onViewReport && (
                <DropdownItem onClick={onViewReport}>View Report</DropdownItem>
              )}

              {/* Edit - opens modal to edit name/description */}
              <DropdownItem onClick={onEdit}>Edit</DropdownItem>

              {/* Clone - disabled while clone is in progress */}
              <DropdownItem onClick={onClone} isDisabled={isCloning}>
                {isCloning ? 'Cloning...' : 'Clone'}
              </DropdownItem>

              {/* Mark as Released - only show for Active status (not archived) */}
              {!isArchived && status === 'Active' && (
                <DropdownItem onClick={onMarkReleased}>Mark as Released</DropdownItem>
              )}

              {/* Mark as Active - only show for Released status (not archived) */}
              {!isArchived && status === 'Released' && (
                <DropdownItem onClick={onMarkActive}>Mark as Active</DropdownItem>
              )}

              {/* Archive/Unarchive toggle */}
              {isArchived ? (
                <DropdownItem onClick={onUnarchive}>Unarchive</DropdownItem>
              ) : (
                <DropdownItem onClick={onArchive}>Archive</DropdownItem>
              )}

              {/* Delete - destructive action */}
              <DropdownItem onClick={onDelete}>Delete</DropdownItem>
            </DropdownItemGroup>
          </DropdownMenu>
        </div>
      </div>

      {/* Description */}
      {description && (
        <p style={{
          fontSize: '14px',
          color: 'var(--ds-text-subtle, #5E6C84)',
          margin: '0 0 6px 0',
          lineHeight: 1.3
        }}>
          {description}
        </p>
      )}

      {/* Stats Row - metadata with subtle dividers between items */}
      <div className="release-card-stats">
        {/* Date */}
        <Tooltip content="Created date">
          <span className="release-card-stat">
            <CalendarIcon label="Date" size="small" />
            <span className="release-card-stat-value">{formatDate(createdOn)}</span>
          </span>
        </Tooltip>

        {/* Folder count */}
        <Tooltip content="Folders">
          <span className="release-card-stat">
            <FolderIcon label="Folders" size="small" />
            <span className="release-card-stat-value">{folderCount}</span> folders
          </span>
        </Tooltip>

        {/* Scenario count */}
        <Tooltip content="Scenarios">
          <span className="release-card-stat">
            <DocumentsIcon label="Scenarios" size="small" />
            <span className="release-card-stat-value">{scenarioCount}</span> scenarios
          </span>
        </Tooltip>

        {/* Run count */}
        <Tooltip content="Test runs">
          <span className="release-card-stat">
            <EditorPanelIcon label="Runs" size="small" />
            <span className="release-card-stat-value">{runCount}</span> runs
          </span>
        </Tooltip>
      </div>

      {/* Progress Bar */}
      <div style={{ marginBottom: '4px' }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          {/* Progress Bar - Shows all 5 statuses, tooltip shows counts */}
          {/* Wrapper div takes flex space; Tooltip's inner span won't break layout */}
          <div style={{ flex: 1 }}>
          <Tooltip content={progressTooltip || 'No executions'}>
            <div style={{
              width: '100%',
              height: '6px',
              borderRadius: '3px',
              backgroundColor: 'var(--ds-background-neutral, #DFE1E6)',
              overflow: 'hidden',
              display: 'flex'
            }}>
              {totalExecutions > 0 ? (
                <>
                  {/* Pass - Green */}
                  {passPercent > 0 && (
                    <div
                      style={{
                        width: `${passPercent}%`,
                        height: '100%',
                        backgroundColor: statusColors.Pass
                      }}
                    />
                  )}
                  {/* Fail - Red */}
                  {failPercent > 0 && (
                    <div
                      style={{
                        width: `${failPercent}%`,
                        height: '100%',
                        backgroundColor: statusColors.Fail
                      }}
                    />
                  )}
                  {/* Blocked - Orange */}
                  {blockedPercent > 0 && (
                    <div
                      style={{
                        width: `${blockedPercent}%`,
                        height: '100%',
                        backgroundColor: statusColors.Blocked
                      }}
                    />
                  )}
                  {/* Query - Blue */}
                  {queryPercent > 0 && (
                    <div
                      style={{
                        width: `${queryPercent}%`,
                        height: '100%',
                        backgroundColor: statusColors.Query
                      }}
                    />
                  )}
                  {/* Exclude - Gray */}
                  {excludePercent > 0 && (
                    <div
                      style={{
                        width: `${excludePercent}%`,
                        height: '100%',
                        backgroundColor: statusColors.Exclude
                      }}
                    />
                  )}
                </>
              ) : null}
            </div>
          </Tooltip>
          </div>

          {/* Percentage */}
          <span style={{
            fontSize: '13px',
            fontWeight: 500,
            color: 'var(--ds-text, #172B4D)',
            minWidth: '45px',
            textAlign: 'right'
          }}>
            {totalExecutions > 0 ? `${Math.round(passPercent)}%` : '-'}
          </span>
        </div>

      </div>
      {/* Note: "Open Release" button removed - card is clickable via onClick={onOpen} */}
    </div>
  )
}

export { ReleaseCard }
